// Story 7.1 (ADO-904) — the Department › Category › Sub-Category drill-state,
// extracted so it can be shared by the standalone `Browse` screen and the
// `/products` page (Epic 7 re-skin, ADO-903), which renders the same nav as a
// header mega-menu (BrowseNav) beside the product listing (CategoryListing).
//
// Selecting (or breadcrumb-navigating to) a node reconciles the whole drill from
// that node's level so the nav columns never contradict the listing: a DEPARTMENT
// resets the Category drill; a CATEGORY sets it; a SUBCATEGORY leaves both
// ancestors intact. Hover/focus REVEALS a column (expandNode) without selecting.

import { useCallback, useState } from 'react';
import type { CategoryNode } from '../../api/browse.client';

export interface BrowseDrill {
  readonly activeDepartment: CategoryNode | null;
  readonly activeCategory: CategoryNode | null;
  readonly selectedNode: CategoryNode | null;
  readonly selectNode: (node: CategoryNode) => void;
  readonly expandNode: (node: CategoryNode) => void;
  readonly navigate: (node: CategoryNode | null) => void;
}

export function useBrowseDrill(): BrowseDrill {
  const [activeDepartment, setActiveDepartment] = useState<CategoryNode | null>(null);
  const [activeCategory, setActiveCategory] = useState<CategoryNode | null>(null);
  const [selectedNode, setSelectedNode] = useState<CategoryNode | null>(null);

  // Hover/focus a Department or Category to reveal its column WITHOUT selecting it
  // (mega-menu drill) — no product listing loads.
  const expandNode = useCallback((node: CategoryNode) => {
    if (node.level === 'DEPARTMENT') {
      setActiveDepartment(node);
      setActiveCategory(null);
    } else if (node.level === 'CATEGORY') {
      setActiveCategory(node);
    }
  }, []);

  const selectNode = useCallback(
    (node: CategoryNode) => {
      expandNode(node);
      setSelectedNode(node);
    },
    [expandNode],
  );

  const navigate = useCallback(
    (node: CategoryNode | null) => {
      if (node === null) {
        // "Shop" root — collapse the whole drill-down.
        setActiveDepartment(null);
        setActiveCategory(null);
        setSelectedNode(null);
        return;
      }
      selectNode(node);
    },
    [selectNode],
  );

  // (2026-07-17 demo feedback) The former `autoLoadFirstDepartment` opt-in is
  // gone: the root state (no selection) now IS a populated listing — the ROOT
  // all-products page — so no default Department is ever preselected.

  return { activeDepartment, activeCategory, selectedNode, selectNode, expandNode, navigate };
}
